import React, { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Workspaces } from "@mui/icons-material";
import { Alert, Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import * as Sentry from "@sentry/react";
import { enqueueSnackbar } from "notistack";

import { LoadingButton } from "ui";

import analytics from "fond/analytics";
import {
  useCreateVersionScenarioConfigurationMutation,
  useGetProjectQuery,
  useUpdateProjectMutation,
  useUpdateVersionScenarioConfigurationMutation,
} from "fond/api";
import { getCurrentProject } from "fond/project";
import { Maybe, Store, VersionScenarioConfiguration } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Modal } from "fond/widgets";

import { convertFormDataToConfig } from "./helper";
import SetupForm, { FormData } from "./SetupForm";

interface SetupModalProps {
  configuration: Maybe<VersionScenarioConfiguration>;
  isReadOnly: boolean;
  onClose: () => void;
}

export const Steps = {
  UPLOAD: 0,
  LAYER_SELECTION: 1,
  CONFIGURATION: 2,
} as const;
export type Steps = (typeof Steps)[keyof typeof Steps];

const steps = ["Upload Artifacts", "Associate Layers", "Configuration"];

const SetupModal: React.FC<SetupModalProps> = ({ configuration, onClose, isReadOnly }) => {
  const [activeStep, setActiveStep] = useState<Steps>(Steps.UPLOAD);
  const [isSaving, setIsSaving] = useState(false);
  const versionId = useSelector((state: Store) => state.project.versionId);
  const [createVersionScenarioConfiguration] = useCreateVersionScenarioConfigurationMutation();
  const [updateVersionScenarioConfiguration] = useUpdateVersionScenarioConfigurationMutation();
  const currentProject = useAppSelector((state) => getCurrentProject(state.project));
  const { data: project } = useGetProjectQuery(currentProject?.ID ?? skipToken);
  const [updateProject] = useUpdateProjectMutation();

  const handleOnCancel = useCallback(() => {
    analytics.track("collaboration.cts.setup.cancelled", {
      versionId,
      currentStep: steps[activeStep],
      isReadOnly,
    });
    onClose();
  }, [versionId, activeStep, isReadOnly, onClose]);

  const handleOnSubmit = useCallback(
    async (formData: FormData) => {
      const payload = convertFormDataToConfig(formData);
      setIsSaving(true);
      const analyticsPayload = {
        versionId,
        isNew: true,
        hasOccupancyFile: payload.Inputs.Occupancy.Type === "file",
        useDefaultBins: formData.DefaultBins,
        binCount: payload.ToolConfigurations.CostToServe.BinCount,
        layerMappingsCount: payload.Inputs.ToolLayersConfiguration.Mappings.length,
      };
      if (!configuration) {
        try {
          await createVersionScenarioConfiguration({ versionId: versionId!, configuration: payload });
          analytics.track("collaboration.cts.configuration.saved", analyticsPayload);
          enqueueSnackbar("Cost to Serve configuration updated", { variant: "success" });
          onClose();
        } catch (e) {
          Sentry.captureException(e);
          enqueueSnackbar("Failed to create Cost to Serve configuration", { variant: "error" });
        } finally {
          setIsSaving(false);
        }
      } else {
        try {
          await updateVersionScenarioConfiguration({ versionId: versionId!, configuration: payload });
          analytics.track("collaboration.cts.configuration.saved", { ...analyticsPayload, isNew: false });
          enqueueSnackbar("Cost to Serve configuration updated", { variant: "success" });
          onClose();
        } catch (e) {
          Sentry.captureException(e);
          enqueueSnackbar("Failed to update Cost to Serve configuration", { variant: "error" });
        } finally {
          setIsSaving(false);
        }
      }

      // update the project config only if the SoM has changed
      if (project && project?.SystemOfMeasurement !== formData.SystemOfMeasurement) {
        setIsSaving(true);
        try {
          await updateProject({ ID: project.ID, SystemOfMeasurement: formData.SystemOfMeasurement }).unwrap();
          analytics.track("Update system of measurement", {
            projectId: project.ID,
            systemOfMeasurement: formData.SystemOfMeasurement,
            source: "scenario-configuration",
          });
        } catch (error) {
          Sentry.captureException(error);
        } finally {
          setIsSaving(false);
        }
      }
    },
    [configuration, createVersionScenarioConfiguration, project, onClose, updateProject, updateVersionScenarioConfiguration, versionId]
  );

  const handleNext = useCallback(() => {
    setActiveStep((prev) => (prev + 1) as Steps);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => (prev - 1) as Steps);
  }, []);

  return (
    <Modal
      open
      disableBackdropClick
      variant="primary"
      header="Import"
      headerIcon={<Workspaces />}
      headerSubtitle="COLLABORATION - COST TO SERVE"
      onClose={handleOnCancel}
      showClose
      showFullscreen
      data-testid="cost-to-serve-settings"
      PaperProps={{ style: { minHeight: "60vh", minWidth: "60vw", maxHeight: "90vh", display: "flex", flexDirection: "column" } }}
      DialogContentProps={{
        sx: {
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          flex: 1,
        },
      }}
      content={
        <>
          {isReadOnly && (
            <Box mb={2}>
              <Alert severity="info">This configuration is read-only.</Alert>
            </Box>
          )}
          <Box sx={{ display: "flex", gap: 4, flex: 1, minHeight: 0 }}>
            <Box sx={{ minWidth: 175 }}>
              <Stepper activeStep={activeStep} orientation="vertical">
                {steps.map((label, index) => (
                  <Step key={label} completed={activeStep > index || isReadOnly}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box sx={{ flex: 1, overflowY: "auto" }} className="customScrollbars">
              <SetupForm
                activeStep={activeStep}
                onNext={handleNext}
                onSubmit={handleOnSubmit}
                configuration={configuration}
                isReadOnly={isReadOnly}
              />
            </Box>
          </Box>
        </>
      }
      actions={
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Button onClick={handleOnCancel} data-testid="close-button">
            Close
          </Button>
          <Box>
            {activeStep !== Steps.UPLOAD && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Back
              </Button>
            )}
            {activeStep === Steps.CONFIGURATION && isReadOnly ? (
              <Button variant="contained" onClick={handleOnCancel} data-testid="readonly-finish-button">
                Finish
              </Button>
            ) : (
              <LoadingButton variant="contained" form="collaboration-cts-form" type="submit" loading={isSaving}>
                {activeStep === Steps.CONFIGURATION ? "Finish" : "Next"}
              </LoadingButton>
            )}
          </Box>
        </Box>
      }
    />
  );
};

export default SetupModal;
